import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CACHE_TIERS } from "@workspace/types";
import { createClient } from "@/lib/supabase/client";
import type { Database } from "@/lib/types/supabase";
import { trpc } from "@/lib/trpc/react";

// Legacy query keys - to be removed as we migrate to tRPC
export const socialKeys = {
	privacy: (userId: string) => ["privacy", userId] as const,
};

// Types
export interface FollowerInteraction {
	id: string;
	username: string | null;
	display_name: string;
	google_avatar_url?: string | null;
	manual_avatar_url?: string | null;
	rating?: number;
	loved?: boolean;
	must_see?: boolean;
	want_to_see?: boolean;
	saw_live?: boolean;
	saw_stream?: boolean;
	content?: string;
}

export interface SocialInteractionsResponse {
	[setId: string]: FollowerInteraction[];
}

export interface Notification {
	id: string;
	type: "follow" | "festival_invite" | "set_review" | "festival_review";
	title: string;
	message: string;
	from_user_id: string;
	from_user_name: string;
	from_user_avatar?: string | null;
	created_at: string;
	read: boolean;
	data?: Record<string, any>;
}

// Note: Only is_private boolean exists in the database
// This simplified interface reflects the actual implementation
export interface PrivacySettings {
	is_private: boolean;
}

/**
 * Consolidated social hook - combines social interactions, notifications, and privacy
 * Replaces: use-social-interactions, use-notifications, use-privacy-settings
 */

// Types for tRPC integration

/**
 * Social interactions for a festival using tRPC
 */
export function useSocialInteractions(
	festivalSlug: string,
	userId: string | null,
): {
	data: SocialInteractionsResponse | undefined;
	isLoading: boolean;
	error: unknown;
} {
	return trpc.festival.getSocialInteractions.useQuery(
		{
			slug: festivalSlug,
			userId: userId!,
		},
		{
			enabled: !!(festivalSlug && userId),
			...CACHE_TIERS.REALTIME, // 2 minutes
			retry: (failureCount, error: any) => {
				// Don't retry on auth errors
				if (error?.data?.code === "UNAUTHORIZED") return false;
				return failureCount < 3;
			},
		},
	);
}

/**
 * Helper function to get interactions for a specific set
 */
export function useSetSocialInteractions(
	festivalSlug: string,
	userId: string | null,
	setId: string,
) {
	const {
		data: allInteractions,
		isLoading,
		error,
	} = useSocialInteractions(festivalSlug, userId);

	return {
		socialInteractions: allInteractions?.[setId] || [],
		isLoading,
		error,
	};
}

/**
 * User notifications using tRPC
 */
export function useNotifications(userId: string | null) {
	const { data: notificationsData } = trpc.social.getNotifications.useQuery(
		{
			page: 1,
			limit: 50,
			includeDismissed: false,
		},
		{
			enabled: !!userId,
			...CACHE_TIERS.REALTIME, // Real-time for notifications
			refetchInterval: 60 * 1000, // Auto-refetch every minute
			retry: (failureCount, error: any) => {
				// Don't retry on auth errors
				if (error?.data?.code === "UNAUTHORIZED") return false;
				return failureCount < 3;
			},
		},
	);

	// Transform tRPC notifications to match the local interface
	const transformedNotifications: Notification[] = (
		notificationsData?.notifications || []
	).map((n) => ({
		id: n.id,
		type: n.type as
			| "follow"
			| "festival_invite"
			| "set_review"
			| "festival_review",
		title: "", // tRPC doesn't have title field, use content.message
		message: n.content.message,
		from_user_id: n.content.followerId || "",
		from_user_name:
			n.content.followerName || n.content.followerUsername || "Unknown",
		from_user_avatar: n.content.followerAvatar || null,
		created_at: n.created_at,
		read: n.read,
		data: n.content,
	}));

	return {
		data: transformedNotifications,
		isLoading: false, // tRPC handles loading state internally
		error: null, // tRPC handles error state internally
	};
}

/**
 * Mark notification as read mutation using tRPC
 */
export function useMarkNotificationReadMutation() {
	const utils = trpc.useUtils();
	const markAsReadMutation = trpc.social.markNotificationsAsRead.useMutation();

	return useMutation({
		mutationFn: async ({
			notificationId,
			userId,
		}: {
			notificationId: string;
			userId: string;
		}) => {
			const result = await markAsReadMutation.mutateAsync({
				notificationIds: [notificationId],
			});

			return result;
		},
		onMutate: async ({ notificationId }) => {
			// Cancel any outgoing refetches
			await utils.social.getNotifications.cancel();
			await utils.social.getNotificationCount.cancel();

			// Snapshot the previous values
			const previousNotifications = utils.social.getNotifications.getData({
				page: 1,
				limit: 50,
				includeDismissed: false,
			});
			const previousCount = utils.social.getNotificationCount.getData();

			// Optimistically update notifications
			if (previousNotifications) {
				utils.social.getNotifications.setData(
					{
						page: 1,
						limit: 50,
						includeDismissed: false,
					},
					(old) => {
						if (!old?.notifications) return old;
						return {
							...old,
							notifications: old.notifications.map((notification) =>
								notification.id === notificationId
									? { ...notification, read: true }
									: notification,
							),
						};
					},
				);
			}

			// Optimistically update count
			if (
				previousCount &&
				!previousNotifications?.notifications?.find(
					(n) => n.id === notificationId,
				)?.read
			) {
				utils.social.getNotificationCount.setData(undefined, (old) => ({
					count: Math.max(0, (old?.count || 0) - 1),
				}));
			}

			return { previousNotifications, previousCount };
		},
		onError: (_err, _variables, context) => {
			// Rollback on error
			if (context?.previousNotifications) {
				utils.social.getNotifications.setData(
					{
						page: 1,
						limit: 50,
						includeDismissed: false,
					},
					context.previousNotifications,
				);
			}
			if (context?.previousCount) {
				utils.social.getNotificationCount.setData(
					undefined,
					context.previousCount,
				);
			}
		},
		onSettled: () => {
			// Always refetch to ensure we have the latest data
			utils.social.getNotifications.invalidate();
			utils.social.getNotificationCount.invalidate();
		},
	});
}

/**
 * Privacy settings
 * NOTE: These granular fields don't exist in the database. Only is_private is implemented.
 * This function always returns default values for backwards compatibility.
 * @deprecated Use the actual is_private field from user profile instead
 */
export function usePrivacySettings(userId: string | null) {
	return useQuery({
		queryKey: ["privacy-settings", userId || ""],
		queryFn: async (): Promise<PrivacySettings> => {
			// In reality, this should fetch from the database
			// For now, return default value
			return {
				is_private: false,
			};
		},
		enabled: !!userId,
		...CACHE_TIERS.REALTIME, // 5 minutes
	});
}

/**
 * Update privacy settings mutation
 */
export function useUpdatePrivacySettingsMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			userId,
			settings,
		}: {
			userId: string;
			settings: Partial<PrivacySettings>;
		}) => {
			const supabase = createClient();

			const { error } = await (supabase
				.from("profiles") as any)
				.update({ is_private: settings.is_private || false })
				.eq("id", userId);

			if (error) throw error;

			return settings;
		},
		onSuccess: (updatedSettings, { userId }) => {
			// Update query cache
			queryClient.setQueryData(
				socialKeys.privacy(userId),
				(
					old: PrivacySettings = {
						is_private: false,
					},
				) => ({
					...old,
					...updatedSettings,
				}),
			);
		},
		onError: (error: Error) => {
			console.error("Error updating privacy settings:", error);
		},
	});
}

/**
 * Utility hooks for tRPC cache invalidation
 */
export function useInvalidateSocial() {
	const utils = trpc.useUtils();

	return {
		invalidateInteractions: (festivalSlug: string, userId: string) => {
			utils.festival.getSocialInteractions.invalidate({
				slug: festivalSlug,
				userId,
			});
		},
		invalidateNotifications: () => {
			utils.social.getNotifications.invalidate();
			utils.social.getNotificationCount.invalidate();
		},
		invalidatePrivacy: (_userId: string) => {
			// Keep this as is since privacy settings aren't migrated to tRPC yet
			// This would need to be updated when privacy settings get their own tRPC procedure
		},
		invalidateAll: () => {
			// Invalidate all social-related tRPC procedures
			utils.social.invalidate();
			utils.festival.getSocialInteractions.invalidate();
			// Note: Festival facepile now uses server actions, no tRPC invalidation needed
		},
	};
}
